import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import after1 from "../../assets/projects/before-after/after1.jpg";
import before1 from "../../assets/projects/before-after/before1.jpg";

// import officeBefore from "../../assets/projects/before-after/office-before.jpg";
// import officeAfter from "../../assets/projects/before-after/office-after.jpg";

const transformations = [
  {
    id: 1,
    title: 'Modern Living Room Makeover',
    after: after1,
    before: before1,
    
    category: 'Residential',
    duration: '6 Weeks',
  },
  {
    id: 2,
    title: 'Office Space Transformation',
    before: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=800&q=80',
    after: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
    category: 'Commercial',
    duration: '8 Weeks',
  },
  {
    id: 3,
    title: 'Bedroom Revival',
    before: 'https://images.unsplash.com/photo-1505693314120-0d443867891c?w=800&q=80',
    after: 'https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=800&q=80',
    category: 'Residential',
    duration: '4 Weeks',
  },
];

export default function BeforeAfter() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50);

  const handleSliderChange = (e) => {
    setSliderPosition(e.target.value);
  };

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % transformations.length);
    setSliderPosition(50);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + transformations.length) % transformations.length);
    setSliderPosition(50);
  };

  const current = transformations[activeSlide];

  return (
    <section className="py-24 md:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-[1px] bg-[#C9A962]" />
            <span className="text-[#C9A962] tracking-[0.3em] text-xs uppercase">Transformations</span>
            <div className="w-12 h-[1px] bg-[#C9A962]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            What if Your Home Looked Like This?
          </h2>
          <p className="text-[#2C2C2C]/60 max-w-2xl mx-auto text-lg">
            Smart updates, quick turnarounds, results that speak for themselves.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Before/After Slider */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative w-full aspect-[16/10] bg-[#FDFBF7] overflow-hidden">
              {/* Before Image */}
              <img
                src={current.before}
                alt="Before"
                className="absolute inset-0 w-full h-full object-contain"
              />

              {/* After Image with Clip */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
              >
                <img
                  src={current.after}
                  alt="After"
                  className="absolute inset-0 w-full h-full object-contain"
                />
              </div>

              {/* Labels */}
              <div className="absolute top-4 left-4 bg-[#2C2C2C]/80 backdrop-blur-sm text-white text-xs px-3 py-2 uppercase tracking-wider">
                After
              </div>
              <div className="absolute top-4 right-4 bg-[#C9A962]/90 backdrop-blur-sm text-white text-xs px-3 py-2 uppercase tracking-wider">
                Before
              </div>

              {/* Slider Control */}
              <div className="absolute inset-0 flex items-center">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={sliderPosition}
                  onChange={handleSliderChange}
                  className="absolute w-full h-full opacity-0 cursor-ew-resize z-20"
                />
                
                {/* Slider Handle */}
                <div
                  className="absolute top-0 bottom-0 w-1 bg-white z-10"
                  style={{ left: `${sliderPosition}%` }}
                >
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center">
                    <ChevronLeft className="w-4 h-4 text-[#2C2C2C] absolute left-1" />
                    <ChevronRight className="w-4 h-4 text-[#2C2C2C] absolute right-1" />
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={prevSlide}
                className="w-10 h-10 border border-[#E8E2D9] flex items-center justify-center hover:border-[#C9A962] hover:text-[#C9A962] transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="flex gap-2">
                {transformations.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setActiveSlide(index);
                      setSliderPosition(50);
                    }}
                    className={`h-2 rounded-full transition-all ${
                      index === activeSlide ? 'w-8 bg-[#C9A962]' : 'w-2 bg-[#E8E2D9]'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={nextSlide}
                className="w-10 h-10 border border-[#E8E2D9] flex items-center justify-center hover:border-[#C9A962] hover:text-[#C9A962] transition-all"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-block px-4 py-2 bg-[#C9A962]/10 text-[#C9A962] text-xs tracking-wider uppercase mb-4">
              {current.category}
            </div>
            
            <h3 className="font-serif text-3xl md:text-4xl text-[#2C2C2C] mb-4">
              {current.title}
            </h3>

            <div className="flex items-center gap-6 mb-8 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#FDFBF7] flex items-center justify-center">
                  <svg className="w-4 h-4 text-[#C9A962]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-[#2C2C2C]/60">Completed in {current.duration}</span>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-6 h-6 bg-[#C9A962]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-3 h-3 text-[#C9A962]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-serif text-lg text-[#2C2C2C] mb-1">Complete Space Redesign</h4>
                  <p className="text-[#2C2C2C]/60 text-sm">Comprehensive planning from floor layout to final styling.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-6 h-6 bg-[#C9A962]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-3 h-3 text-[#C9A962]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-serif text-lg text-[#2C2C2C] mb-1">Premium Materials & Finishes</h4>
                  <p className="text-[#2C2C2C]/60 text-sm">Curated selection of quality materials within budget.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-6 h-6 bg-[#C9A962]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-3 h-3 text-[#C9A962]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-serif text-lg text-[#2C2C2C] mb-1">Quick Turnaround Time</h4>
                  <p className="text-[#2C2C2C]/60 text-sm">Efficient execution without compromising on quality.</p>
                </div>
              </div>
            </div>

            <a
              href="#contact"
              className="inline-flex items-center bg-[#C9A962] text-white px-8 py-4 hover:bg-[#b8954f] transition-colors group"
            >
              Transform Your Space
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}